import { canManageClients, canViewClientBilling } from '@/shared/rbac'
import type { Tour } from '../../types'
import { target } from './target'

/** `/clients/<id>/<tab>` for one tab. */
const CLIENT_TAB = (tab: string) => new RegExp(`^/clients/[^/]+/${tab}/?$`)

export const adminTours: Tour[] = [
  {
    id: 'page:clients',
    version: 1,
    title: 'Clients',
    kind: 'page',
    match: /^\/clients\/?$/,
    access: { gate: 'canViewClients' }, // router: clients/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Clients', body: 'Every client you can see, with its facilities, providers and users.' },
      { id: 'view', target: target('clients.view'), side: 'bottom', title: 'Table or grid', body: 'Switch how the list is laid out.' },
      { id: 'add', target: target('clients.add'), access: { when: canManageClients }, side: 'bottom', title: 'Add Client', body: 'Create a new client record.' },
      { id: 'open', title: 'Open a client', body: 'Click a client to open its record, with tabs for details, specialties, facilities, providers and users.' },
    ],
  },
  {
    id: 'page:client-record',
    version: 3,
    title: 'Client record',
    kind: 'page',
    match: /^\/clients\/[^/]+\/[^/]+\/?$/,
    access: { gate: 'canViewClients' }, // router: clients/*
    // Steps are scoped per tab with `onlyOn` (the tab is the last path segment).
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Client record', body: 'Everything about this client on one page.' },
      // Split on the page's own billing-tab rule (client-details.tsx canViewClientBilling).
      { id: 'tabs', target: target('client.tabs'), access: { when: (a) => !canViewClientBilling(a) }, side: 'bottom', title: 'Record tabs', body: '"Details", "Specialties", "Facilities", "Providers" and "Users" describe the client.' },
      { id: 'tabs-billing', target: target('client.tabs'), access: { when: canViewClientBilling }, side: 'bottom', title: 'Record tabs', body: '"Details", "Specialties", "Facilities", "Providers" and "Users" describe the client; "Invoices" and "Contracts & pricing" hold its billing.' },
      { id: 'details', onlyOn: CLIENT_TAB('details'), title: 'Details', body: 'The client\'s own details.' },
      { id: 'specialties', onlyOn: CLIENT_TAB('specialties'), title: 'Specialties', body: 'The specialties this client\'s charts are coded in.' },
      { id: 'facilities', onlyOn: CLIENT_TAB('facilities'), title: 'Facilities', body: 'The client\'s facilities.' },
      { id: 'providers', onlyOn: CLIENT_TAB('providers'), title: 'Providers', body: 'The providers whose charts come from this client.' },
      { id: 'users', onlyOn: CLIENT_TAB('users'), title: 'Users', body: 'The people with access to this client.' },
      // Billing tabs render the same pages as Finance Overview, scoped to this client.
      // Anchored on the filter control, not the month: this step is gated on
      // canViewClientBilling, and an admin's month moved into the filter
      // drawer on 2026-10-27, so timesheet.month is never in the DOM for
      // anyone who reaches this step.
      { id: 'timesheet', target: target('timesheet.filters'), onlyOn: CLIENT_TAB('timesheet'), access: { when: (a) => canViewClientBilling(a) && a.can('timesheet', 'read') }, side: 'bottom', title: 'Timesheet', body: 'This client\'s logged and imported hours. Change the month, facility and the rest in Filters.' },
      { id: 'invoices', target: target('invoices.filters'), onlyOn: CLIENT_TAB('invoices'), access: { when: canViewClientBilling }, waitMs: 2500, side: 'bottom', title: 'Invoices', body: 'This client\'s invoices. Narrow them by facility, scope and billing period.' },
      { id: 'pricing', onlyOn: CLIENT_TAB('pricing'), access: { when: canViewClientBilling }, title: 'Contracts & pricing', body: 'What this client is charged. This is where the client\'s rates are kept.' },
    ],
  },
  {
    id: 'page:access',
    version: 2,
    title: 'Access Requests',
    kind: 'page',
    match: /^\/access(\/requests)?\/?$/,
    access: { when: (a) => a.can('access_request', 'read') }, // router: access/*
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Access Requests', body: 'Request access, track approvals and manage active grants.' },
      // Facility requests: only users the server lets file them (`/auth/me` access_request_types).
      { id: 'new', target: target('access.new'), access: { when: (a) => !a.canRequestFacilityChange }, side: 'bottom', title: 'New request', body: 'Ask for temporary access to unmasked patient details for a set number of hours.' },
      { id: 'new-facility', target: target('access.new'), access: { when: (a) => a.canRequestFacilityChange }, side: 'bottom', title: 'New request', body: 'Ask for another facility, or for temporary access to unmasked patient details for a set number of hours.' },
      { id: 'tabs', target: target('access.tabs'), side: 'bottom', title: 'Track it', body: '"My requests" shows what you asked for, "To approve" what\'s waiting for you, and "Active access" what\'s currently granted.', bodyFrom: (c) => (c.access.canAdminSystem ? '"My requests" shows what you asked for, "To approve" what\'s waiting for you, "Active access" what\'s currently granted, and "Audit log" every request and decision.' : '') },
    ],
  },
  {
    id: 'page:system-admin-clients',
    version: 1,
    title: 'Clients & Facilities',
    kind: 'page',
    match: /^\/system-admin(\/clients)?\/?$/,
    access: { gate: 'canAdminSystem' }, // router: system-admin/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Clients & Facilities', body: 'Every client on the platform and its facilities.' },
      { id: 'tabs', target: target('sysadmin.tabs'), side: 'bottom', title: 'System Admin areas', body: 'Clients & facilities, providers, users, roles, query templates, query health and the audit log each have their own tab.' },
      { id: 'search', target: target('sysadmin.search'), side: 'bottom', title: 'Find a client', body: 'Search by client or facility name.' },
    ],
  },
  {
    id: 'page:system-admin-users',
    version: 1,
    title: 'Users',
    kind: 'page',
    match: /^\/system-admin\/users\/?$/,
    access: { gate: 'canAdminSystem' },
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Users', body: 'Everyone with an account, with their roles and status.' },
      { id: 'status', target: target('users.status'), side: 'bottom', title: 'Filter by status', body: 'Switch between active and inactive accounts.' },
      { id: 'add', target: target('users.add'), side: 'bottom', title: 'Add User', body: 'Creates an account and sends the invite email. Row actions let you resend an invite or view a user\'s audit log.' },
    ],
  },
  {
    id: 'page:system-admin-roles',
    version: 1,
    title: 'Roles',
    kind: 'page',
    match: /^\/system-admin\/roles\/?$/,
    access: { gate: 'canAdminSystem' },
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Roles', body: 'What each role can read and edit. Built-in roles are read-only; to customize one, create a "New Role" and choose it under "Clone permissions from".' },
    ],
  },
  {
    id: 'page:announcements',
    version: 1,
    title: 'Announcements',
    kind: 'page',
    match: /^\/announcements\/?$/,
    access: { gate: 'canViewAnnouncements' }, // router: announcements/*
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Announcements', body: 'Product, guideline, payer and maintenance updates. Some must be acknowledged before they\'re cleared.' },
      { id: 'severity', target: target('announcements.severity'), side: 'bottom', title: 'Severity', body: 'Show only critical, important or informational updates.' },
      { id: 'search', target: target('announcements.search'), side: 'bottom', title: 'Search updates', body: 'Find an update by its title or text.' },
    ],
  },
  {
    id: 'page:settings',
    version: 3,
    title: 'Settings',
    kind: 'page',
    match: /^\/settings(\/(profile|security|sessions|notifications))?\/?$/,
    // router: settings/* has no guard (any signed-in user).
    mayHoldEdits: true, // profile inline edit mode, notification prefs
    steps: [
      { id: 'tabs', target: target('settings.tabs'), side: 'bottom', title: 'Settings', body: '"Profile" holds your name and display preferences, "Security" your password and sign-in method, "Sessions" where you\'re signed in, and "Notifications" what reaches you in-app or by email.' },
      { id: 'page', target: target('page-header'), waitMs: 2500, side: 'bottom', title: 'This tab', body: 'Everything on this tab applies to your own account only.' },
    ],
  },
]
